// frontend/src/api.js
import axios from 'axios';
import { getCurrentLanguage, normalizeLanguage } from './i18n';

// Lógica dinámica para asegurar el prefijo /api
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';
const baseURL = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;


const ENERGY_UPDATE_RULES = [
  { method: 'post', pattern: /^\/agent\/intent$/ },
  { method: 'post', pattern: /^\/qoph$/ },
  { method: 'post', pattern: /^\/qoph\/[^/]+\/messages$/ },
  { method: 'post', pattern: /^\/mutant-box$/ },
  { method: 'post', pattern: /^\/mutant-box\/generate$/ },
  { method: 'get', pattern: /^\/mutant-box\/[^/]+\/context$/ },
  { method: 'post', pattern: /^\/mutant-box\/[^/]+\/actions$/ },
  { method: 'get', pattern: /^\/tareas\/generate$/ },
  { method: 'post', pattern: /^\/tareas\/generate$/ },
  { method: 'get', pattern: /^\/tareas\/guide$/ },
  { method: 'post', pattern: /^\/tareas\/guide$/ },
  { method: 'post', pattern: /^\/tareas\/generator\/generate$/ },
  { method: 'post', pattern: /^\/tareas$/ },
  { method: 'post', pattern: /^\/profile\/feedback$/ },
  { method: 'get', pattern: /^\/profile\/summary\/regenerate$/ },
  { method: 'post', pattern: /^\/profile\/summary\/regenerate$/ },
  { method: 'get', pattern: /^\/questionnaire\/generate$/ },
  { method: 'post', pattern: /^\/questionnaire\/responses$/ },
  { method: 'post', pattern: /^\/conciencia\/generate$/ },
  { method: 'post', pattern: /^\/conciencia\/response$/ },
  { method: 'post', pattern: /^\/conciencia\/regenerate$/ },
  { method: 'post', pattern: /^\/subconciencia\/generate$/ },
  { method: 'post', pattern: /^\/subconciencia\/response$/ },
  { method: 'post', pattern: /^\/subconciencia\/regenerate$/ },
  { method: 'post', pattern: /^\/ensonacion\/generate$/ },
  { method: 'post', pattern: /^\/reverie\/generate$/ }
];

function normalizeApiPath(url = '') {
  try {
    const parsed = new URL(url, baseURL);
    let path = parsed.pathname || '';
    if (path.startsWith('/api/')) path = path.slice(4);
    if (path === '/api') path = '/';
    return path;
  } catch (_) {
    const raw = String(url || '').split('?')[0];
    return raw.startsWith('/api/') ? raw.slice(4) : raw;
  }
}

function shouldDispatchEnergyUpdate(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  const path = normalizeApiPath(config.url || '');
  if (path === '/agent/stamina') return false;
  return ENERGY_UPDATE_RULES.some(rule => rule.method === method && rule.pattern.test(path));
}

function dispatchEnergyUpdated() {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new Event('q:energy-updated'));
}

const api = axios.create({
  baseURL: baseURL, // Usamos la URL calculada con /api
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Mantener los interceptores existentes si los hubiera, o usar estos:
api.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const language = getCurrentLanguage();
      config.headers['X-Q-Language'] = language;
      config.headers['Accept-Language'] = language;
    } catch (_) { /* ignore localStorage errors */ }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (shouldDispatchEnergyUpdate(response.config)) {
      setTimeout(dispatchEnergyUpdated, 0);
    }
    return response;
  },
  (error) => Promise.reject(error)
);

/* ---------------------------
   Notificaciones / Questionnaire
   --------------------------- */
export function fetchNotifications(context) {
  return api.get('/notifications', { params: context ? { context } : {} });
}
export function markNotificationRead(id) {
  return api.post(`/notifications/${encodeURIComponent(id)}/read`);
}
export function markAllNotificationsRead() {
  return api.post('/notifications/read-all');
}
export function deleteAllNotifications() {
  return api.delete('/notifications/all');
}
export function deleteNotificationsBatch(ids) {
  return api.post('/notifications/batch/delete', { ids });
}
export function fetchNotificationAudio(id) {
  return api.get(`/notifications/${encodeURIComponent(id)}/audio`, { responseType: 'blob' });
}

export function shouldShowQuestionnaire() {
  return api.get('/questionnaire/should-show');
}
export function fetchQuestionnaire() {
  return api.get('/questionnaire/generate');
}
export function postQuestionnaireResponses(payload) {
  return api.post('/questionnaire/responses', payload);
}
export function postSkipQuestionnaire() {
  return api.post('/questionnaire/skip');
}

/* ---------------------------
   Perfil
   --------------------------- */
export function fetchProfileSummary() {
  return api.get('/profile/summary');
}
export function regenerateProfileSummary() {
  return api.post('/profile/summary/regenerate');
}
export function sendProfileFeedback(feedback) {
  return api.post('/profile/feedback', { feedback });
}
export const fetchNuclearPrinciples = () => api.get('/profile/nuclear-principles', { params: { lang: getCurrentLanguage() } });
export const fetchQContexts = () => api.get('/profile/q-contexts');

/* ---------------------------
   Conciencia
   --------------------------- */
export function generateConcienciaQuestion(data) {
  // data opcional: { translateTo, ... }
  const lang = getCurrentLanguage();
  return api.post('/conciencia/generate', { lang, translateTo: lang, ...(data || {}) });
}
export function postConcienciaResponse(payload) {
  return api.post('/conciencia/response', { lang: getCurrentLanguage(), ...(payload || {}) });
}
export function postConcienciaVoice(formData) {
  try { formData.append('lang', getCurrentLanguage()); } catch (_) {}
  return api.post('/conciencia/voice', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}
export function regenerateConcienciaQuestion(payload) {
  const lang = getCurrentLanguage();
  return api.post('/conciencia/regenerate', { lang, translateTo: lang, ...(payload || {}) });
}

/* ---------------------------
   Subconciencia
   --------------------------- */
export function generateSubconcienciaQuestion(data) {
  const lang = getCurrentLanguage();
  return api.post('/subconciencia/generate', { lang, translateTo: lang, ...(data || {}) });
}
export function postSubconcienciaResponse(payload) {
  return api.post('/subconciencia/response', { lang: getCurrentLanguage(), ...(payload || {}) });
}
export function regenerateSubconcienciaQuestion(payload) {
  const lang = getCurrentLanguage();
  return api.post('/subconciencia/regenerate', { lang, translateTo: lang, ...(payload || {}) });
}
export function getSubconcienciaNeuralDashboard(page = 1, limit = 20, lang = 'es') {
  return api.get(`/subconciencia/neural-dashboard?page=${page}&limit=${limit}&lang=${lang}`);
}

/* ---------------------------
   Ensoñación / Reverie
   --------------------------- */
export function fetchActiveReverie() {
  return api.get('/reverie/active', { params: { lang: getCurrentLanguage(), _: Date.now() } });
}
export function generateReverieImage(payload = {}) {
  const lang = getCurrentLanguage();
  return api.post('/reverie/generate', { lang, translateTo: lang, ...(payload || {}) });
}
export function saveReverieReflection(id, reflection) {
  const lang = getCurrentLanguage();
  return api.post(`/reverie/${encodeURIComponent(id)}/reflection`, { lang, translateTo: lang, reflection });
}

/* ---------------------------
   Tareas
   --------------------------- */
export function generateTasks() {
  return api.get('/tareas/generate', { params: { lang: getCurrentLanguage(), translateTo: getCurrentLanguage() } });
}
export function getAcceptedTask() {
  return api.get('/tareas/accepted');
}
export function saveTaskResponse(payload) {
  return api.post('/tareas', payload);
}
export function getTaskHistory() {
  return api.get('/tareas/history');
}
export function generateTaskGuide(mainTask, refinement) {
  const lang = getCurrentLanguage();
  return api.post('/tareas/guide', { mainTask, refinement, lang, translateTo: lang });
}
export function getTaskById(id) {
  return api.get(`/tareas/${encodeURIComponent(id)}`);
}
export function generateTaskFromPrompt(prompt, title) {
  const lang = getCurrentLanguage();
  const payload = { prompt, lang, translateTo: lang };
  if (title && String(title).trim()) payload.title = title;
  return api.post('/tareas/generator/generate', payload);
}

/* ---------------------------
   Users / audio
   --------------------------- */
export function fetchMe() {
  return api.get('/users/me');
}
export function setAudioEnabled(enabled) {
  return api.put('/users/audio-enabled', { audioEnabled: !!enabled });
}
export function setPreferredLanguage(language) {
  return api.put('/users/me/language', { language: normalizeLanguage(language) });
}

export function fetchAudioFromCategory(category) {
  return api.get(`/audio/category/${encodeURIComponent(category)}`, { responseType: 'blob' });
}
export function generateAudioFromText(text, lang = null) {
  return api.post('/audio/generate', { text, lang: normalizeLanguage(lang || getCurrentLanguage()) }, { responseType: 'blob' });
}
export function generateAudioFromTextUser(text, lang = null) {
  return api.post('/users/me/audio', { text, lang: normalizeLanguage(lang || getCurrentLanguage()) }, { responseType: 'blob' });
}
export function fetchAudioFromCategoryUser(category) {
  return api.get('/users/me/audio', { params: { category }, responseType: 'blob' });
}

/* ---------------------------
   Mutant Box
   --------------------------- */

/**
 * Obtener la caja (incluye box.contents si el backend lo devuelve).
 * GET /api/mutant-box
 * Acepta params opcionales, por ejemplo { userId }
 */
export function fetchMutantBox(params = {}) {
  return api.get('/mutant-box', { params: { lang: getCurrentLanguage(), ...params } });
}

/**
 * Obtener contenidos de una caja concreta (paginado).
 * GET /api/mutant-box/:boxId/content?limit=&offset=
 */
export function fetchMutantBoxContent(boxId, params = {}) {
  return api.get(`/mutant-box/${encodeURIComponent(boxId)}/content`, { params });
}

export function fetchMutantBoxContext(boxId) {
  return api.get(`/mutant-box/${encodeURIComponent(boxId)}/context`, { params: { lang: getCurrentLanguage() } });
}

/**
 * Obtener acciones (si existe endpoint server-side para listarlas)
 * GET /api/mutant-box/actions
 */
export function fetchMutantActions(params) {
  return api.get('/mutant-box/actions', { params });
}

/**
 * Generar contenido para el usuario.
 * POST /api/mutant-box/generate
 * - payload puede incluir flags: { translateTo, triggerBy, requestIp, ... }
 */
export function generateMutantBox(payload = {}) {
  const lang = getCurrentLanguage();
  return api.post('/mutant-box/generate', { lang, translateTo: lang, ...payload });
}

/**
 * Crear acción en la caja
 * POST /api/mutant-box/:boxId/actions
 * - action debe contener { contentId, actionType, metadata }
 */
export function postMutantBoxAction(boxId, action) {
  const lang = getCurrentLanguage();
  return api.post(`/mutant-box/${encodeURIComponent(boxId)}/actions`, { lang, translateTo: lang, ...(action || {}) });
}

export function fetchJobStatus(jobId) {
  return api.get(`/jobs/${encodeURIComponent(jobId)}`);
}

export function sendContactMessage(payload) {
  return api.post('/contact', payload);
}

// Gamification Phase 1: Progression
export function fetchAgentTip(lang) {
  const query = lang ? `?lang=${lang}` : '';
  return api.get(`/agent/tips${query}`);
}

export function fetchProgressionState() {
  return api.get('/progression/state');
}

export function advanceProgressionStage(nextStage) {
  return api.post('/progression/advance', { nextStage });
}

/* alias por compatibilidad */
export { fetchMutantBox as fetchMutantBoxContentAlias };
export { generateMutantBox as generateMutantBoxAlias };

export default api;
